import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type Dispatch, type MouseEvent, type MutableRefObject, type ReactNode, type SetStateAction } from 'react'
import { CalendarClock, ChevronDown, ChevronUp, FileEdit, Plus, RefreshCw, Rocket, ShieldCheck, Trash2, X } from 'lucide-react'
import { Link, revenirOuRemplacer } from '../router.js'
import { Modal } from '../admin/primitives.js'
import { Checkbox } from '../admin/form.js'
import { chargerListe, chargerSalles, creerSoiree, gesteSoiree } from './soirees-maison-api.js'
import { useMoi, usePreferencesBilletterie, useTvaDefautBilletterie } from './soirees-maison-hooks.js'
import {
  CANAL_LIBELLE, DROIT_PUBLIER, GardeCles, NOM_BILLETTERIE, PARIS_RX, PHRASE_NUIT_PASSEE,
  bpDepuisSaisie, centsDepuisSaisie, champLisible, euros, doublonsDe, heureDeNuitLisible, jourCivil, libelleChoixHeure, maintenantParis, nuitEnLettres, nuitPassee, parisLisible,
  phraseDoublon, raisonEcriture, stockageMemoire, stockageSession, tvaLisible,
  type Ambiguite, type ErreurBM, type ReponseListe, type Salle, type SoireeListe,
} from './soirees-maison-model.js'
import {
  ETAPES, PHRASE_AFFICHE_NON_ENVOYEE, PHRASE_RANGEMENT, avertissementsTarifAssistant, brouillonEntame, brouillonInitial, brouillonRelu, corpsCreation,
  creerPuisEnvoyerAffiche, dureeMinutes, envoyerAfficheSeule, erreursEtape, erreursTarifAssistant, phraseVenteImmediate, premiereEtapeBloquante,
  premiereVenteSurPlace, rangerBrouillon, retenus, rienAVendre, tarifEffectif, tarifLibre,
  type BrouillonAssistant, type ChoixVente, type EtatAffiche, type RangementBrouillon, type TarifAssistant,
} from './assistant-soiree-model.js'
import { cadreInitial, type AfficheBrouillon, type Cadre } from './affiche-model.js'
import { libererSource, recadrer, type SourceAffiche } from './affiche-navigateur.js'
import { FondAffiche, NetAffiche, RecadrageAffiche, envoyerAfficheRecadree } from './AfficheSoiree.js'
import { resumeSaisie } from './tarif-saisie-model.js'
import { heureRelativeLisible } from './reglages-billetterie-model.js'
import { TarifChamps } from './TarifChamps.js'
import { illustrationSalle } from './salles-illustrations.js'

/**
 * L'ASSISTANT « NOUVELLE SOIRÉE » — une FENÊTRE par-dessus la page Soirées
 * (22/10/2026, contrat `soirees-v1` §9.6).
 *
 * « Le même design que l'ouverture d'une main courante » (Hugo, 22/10) : la
 * grammaire est celle de `soiree-wizard.js` et `wizard.css` de la Main
 * Courante, lus seulement — fenêtre sur fond voilé, surtitre et question par
 * étape, jalons numérotés qui se cochent (le ✓ se trace) et restent
 * cliquables une fois visités, barre qui avance avec une impulsion, étapes qui
 * glissent dans le sens de la marche, champs qui montent en cascade, tuiles
 * illustrées, pied « Retour · Étape n sur N · Continuer », bouton final qui
 * bat, croix qui tourne, Échap ferme, Entrée continue (pas dans un texte
 * long), secousse sur une erreur, écran final avec halo. Les valeurs, elles,
 * sont celles du design system : jetons, mouvement `--dur-*` / `--ease-out`,
 * accent produit.
 *
 * La première étape est L'AFFICHE, comme sur Shotgun (on peut la passer :
 * « Plus tard ») ; dès la deuxième, elle s'affiche en fondu en tête de la
 * fenêtre, recette du bandeau de la clôture de la compta.
 *
 * La logique ne change pas : rien d'écrit avant la fin ; la soirée et ses
 * tarifs partent en UN envoi, en brouillon ; puis l'affiche, par son propre
 * envoi ; puis la mise en vente choisie, avec confirmation d'identité. Si l'une
 * des deux suites échoue, l'écran final le dit et ne refait qu'elle : la
 * soirée n'est jamais recréée.
 *
 * Le brouillon (affiche comprise, en data URL) et la clé d'idempotence de la
 * création vivent dans `sessionStorage`, rangés par personne : après une
 * issue incertaine, on recharge souvent la page — le même contenu repart avec
 * la même clé et ne crée rien de plus (§3.7).
 *
 * La fenêtre est PORTÉE dans `body` (primitive `Modal`) : `.lc-route` porte un
 * `transform`, qui piégerait une surface fixe.
 */

const CLE_CREATION = 'creation-soiree'
/** La durée de sortie d'une étape (`--dur-nav`) : l'étape sortante quitte ensuite le document. */
const SORTIE_MS = 320

type Fin = {
  soiree_id: string; version: number; nom: string; vente: ChoixVente; a: string; etat: 'brouillon' | 'envoi' | 'faite' | 'echec'; erreur: ErreurBM | null
  /** « Mettre en vente tout de suite » : ce que la caisse vend vraiment (un tarif sur place qui commence à l'ouverture ne se vend pas tout de suite). */
  phrase_vente: string
  /** L'affiche part APRÈS la création (§9.6) ; son échec se réessaie seul. */
  affiche: EtatAffiche
  /** L'affiche à renvoyer (« Réessayer ») : gardée en mémoire, le brouillon est déjà oublié. */
  affiche_a_envoyer: AfficheBrouillon | null
}

/* ═══════════════════════ ouvrir la fenêtre ═══════════════════════ */

/** La fenêtre est un ÉTAT de la page, pas une page : `/soirees/nouvelle` l'ouvre d'entrée. */
export function useAssistantSoiree(ouvertAuDepart = false): { ouvert: boolean; tour: number; ouvrir: () => void; fermer: () => void } {
  const [ouvert, setOuvert] = useState(ouvertAuDepart)
  const [tour, setTour] = useState(0)
  const ouvrir = useCallback(() => { setTour((n) => n + 1); setOuvert(true) }, [])
  const fermer = useCallback(() => setOuvert(false), [])
  return { ouvert, tour, ouvrir, fermer }
}

/**
 * « Nouvelle soirée » : un vrai lien (ouvrable dans un autre onglet, qui
 * arrive sur `/soirees/nouvelle`), qui ouvre la fenêtre sur place.
 */
export function LienNouvelleSoiree({ ouvrir }: { ouvrir: () => void }) {
  const clic = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
    e.preventDefault()
    ouvrir()
  }
  return <a href="/soirees/nouvelle" className="lc-btn lc-btn--primary" onClick={clic}><Plus size={15} aria-hidden="true" /> Nouvelle soirée</a>
}

/**
 * La fenêtre. Pendant un envoi, ni Échap ni un clic sur le voile ne la
 * ferment : la soirée peut être créée à l'instant même, et son résultat
 * n'aurait plus d'écran où s'afficher. Arrivée par `/soirees/nouvelle`, elle
 * rend l'adresse `/soirees` une fois refermée.
 */
export function AssistantSoiree(props: { ouvert: boolean; tour: number; onFermer: () => void; onCreee?: (() => void) | undefined }) {
  const verrou = useRef(false)
  const aEteOuvert = useRef(false)
  useEffect(() => { if (props.ouvert) aEteOuvert.current = true }, [props.ouvert])
  const fermer = useCallback(() => { if (!verrou.current) props.onFermer() }, [props.onFermer]) // eslint-disable-line react-hooks/exhaustive-deps
  const sortie = () => {
    if (aEteOuvert.current && window.location.pathname === '/soirees/nouvelle') revenirOuRemplacer('/soirees')
  }
  return (
    <Modal open={props.ouvert} onClose={fermer} onExited={sortie} owning ariaLabel="Nouvelle soirée" className="bsw">
      <ContenuAssistant key={props.tour} fermer={fermer} verrou={verrou} onCreee={props.onCreee} />
    </Modal>
  )
}

/* ═══════════════════════ le parcours ═══════════════════════ */

function ContenuAssistant({ fermer, verrou, onCreee }: { fermer: () => void; verrou: MutableRefObject<boolean>; onCreee?: (() => void) | undefined }) {
  const moi = useMoi()
  const { prefs, lues, erreur: erreurPrefs, relire: relirePrefs } = usePreferencesBilletterie()
  // Le taux que prendra un tarif créé sans taux : dit avant la création, jamais inventé.
  const tvaDefautBp = useTvaDefautBilletterie()
  const [salles, setSalles] = useState<Salle[] | null>(null)
  const [contexte, setContexte] = useState<ReponseListe | null>(null)
  const [erreurLecture, setErreurLecture] = useState<string | null>(null)
  const [tourLecture, setTourLecture] = useState(0)
  const [b, setB] = useState<BrouillonAssistant | null>(null)
  const [retrouve, setRetrouve] = useState(false)
  const [etape, setEtape] = useState(0)
  /** La dernière étape atteinte : les jalons jusqu'à elle restent cliquables. */
  const [max, setMax] = useState(0)
  const [sens, setSens] = useState<'avant' | 'arriere' | null>(null)
  const [sortante, setSortante] = useState<number | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<ErreurBM | null>(null)
  const [ambiguites, setAmbiguites] = useState<Ambiguite[]>([])
  const [fin, setFin] = useState<Fin | null>(null)
  const [memeNuit, setMemeNuit] = useState<SoireeListe[]>([])
  /** La raison d'un « Continuer » refusé, sous l'étape (la secousse la montre). */
  const [pourquoi, setPourquoi] = useState<string | null>(null)
  const [secousse, setSecousse] = useState(0)
  const [rangement, setRangement] = useState<RangementBrouillon>('complet')
  // L'affiche : l'image d'origine reste en mémoire le temps de la fenêtre ; seule l'affiche recadrée va au brouillon.
  const [source, setSource] = useState<SourceAffiche | null>(null)
  const [cadre, setCadre] = useState<Cadre | null>(null)
  const [cadreRecadre, setCadreRecadre] = useState(true)
  const [preparation, setPreparation] = useState(false)
  const [refusAffiche, setRefusAffiche] = useState<string | null>(null)
  const memoire = useRef(stockageMemoire())
  const cles = useMemo(() => new GardeCles(memoire.current, moi ?? 'inconnu'), [moi])
  const racine = useRef<HTMLDivElement>(null)
  const corps = useRef<HTMLDivElement>(null)
  const contenu = useRef<HTMLDivElement>(null)
  const minuterie = useRef<ReturnType<typeof setTimeout>>(undefined)
  const sourceLue = useRef<SourceAffiche | null>(null)
  sourceLue.current = source
  const cleBrouillon = `billetterie-soirees.assistant.${moi ?? 'inconnu'}`

  useEffect(() => () => { clearTimeout(minuterie.current); libererSource(sourceLue.current) }, [])

  useEffect(() => {
    setErreurLecture(null)
    void chargerSalles().then((r) => (r.ok ? setSalles(r.data) : setErreurLecture(r.erreur.message)))
    void chargerListe({ limite: '1', entrainement: '0' }).then((r) => (r.ok ? setContexte(r.data) : setErreurLecture(r.erreur.message)))
  }, [tourLecture])

  // Le départ : un brouillon non envoyé de cet onglet, sinon les réglages de Gestion.
  useEffect(() => {
    if (b || !salles || !contexte || !lues || !moi) return
    const relu = brouillonRelu(stockageSession().getItem(cleBrouillon))
    if (relu && brouillonEntame(relu)) { setB(relu); setRetrouve(true) } else setB(brouillonInitial(prefs, salles, contexte.nuit_proposee))
  }, [salles, contexte, lues, moi]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!b || fin) return
    setRangement(rangerBrouillon(stockageSession(), cleBrouillon, b))
  }, [b, fin, cleBrouillon])

  // §4.5 : une soirée de même nom la même nuit ? On lit la nuit choisie, entraînement compris.
  const nuit = b?.nuit ?? ''
  useEffect(() => {
    if (!jourCivil(nuit)) { setMemeNuit([]); return }
    let vivant = true
    void chargerListe({ depuis: nuit, jusqua: nuit, entrainement: '1', limite: '50', ordre: 'croissant' })
      .then((r) => { if (vivant) setMemeNuit(r.ok ? r.data.soirees : []) })
    return () => { vivant = false }
  }, [nuit])

  // La secousse de l'étape fautive : rejouée à chaque refus, sur l'étape À L'ÉCRAN (après le rendu).
  useEffect(() => {
    const el = contenu.current
    if (!secousse || !el) return
    el.classList.remove('bsw-secoue'); void el.offsetWidth; el.classList.add('bsw-secoue')
  }, [secousse])

  // Le champ qui avait le focus part avec l'étape sortante (inerte) : le focus revient à la fenêtre,
  // sinon la touche Entrée suivante tomberait dans le vide. Un champ `autoFocus` de la nouvelle étape le garde.
  useEffect(() => {
    const r = racine.current
    const actif = document.activeElement instanceof HTMLElement ? document.activeElement : null
    if (r && (!actif || !r.contains(actif) || actif.closest('[inert]'))) (r.closest('.lc-modal') as HTMLElement | null)?.focus({ preventScroll: true })
  }, [etape, fin])

  // Entrée continue — sauf dans un texte long, sur un bouton (il s'active) ou un contrôle qui la prend.
  const suivantLu = useRef<() => void>(() => undefined)
  useEffect(() => {
    const touche = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.defaultPrevented || e.isComposing || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return
      const r = racine.current
      const cible = e.target instanceof HTMLElement ? e.target : null
      if (!r || !cible) return
      if (!(r.contains(cible) || cible === r.closest('.lc-modal'))) return
      if (cible.closest('textarea, button, a, [role="checkbox"], [role="radio"], [role="tab"], [role="combobox"], [role="listbox"], [contenteditable="true"]')) return
      e.preventDefault()
      suivantLu.current()
    }
    document.addEventListener('keydown', touche)
    return () => document.removeEventListener('keydown', touche)
  }, [])

  const maj = (f: (x: BrouillonAssistant) => BrouillonAssistant) => { setB((x) => (x ? f(x) : x)); setErreur(null); setPourquoi(null) }
  const secouer = (raison: string | null) => { setPourquoi(raison); setSecousse((n) => n + 1) }

  const pret = !!b && !!contexte && !!salles
  const actions = contexte?.actions ?? null
  const blocage = !actions ? null : !actions.preparer ? 'Il vous faut le droit « Préparer les soirées de Billetterie Live Club ».' : raisonEcriture(contexte!.ecriture)
  const cle = ETAPES[etape]!.cle
  const doublons = b ? doublonsDe(memeNuit, b.nom, b.nuit) : []
  const passee = b && contexte ? nuitPassee(b.nuit, contexte.nuit_proposee) : false

  /** Quitter l'étape de l'affiche : l'image d'origine est recadrée une fois, ici (le `canvas` ne sert qu'à ce moment). */
  const afficheValidee = async (): Promise<boolean> => {
    if (!source || !cadre || cadreRecadre) return true
    setPreparation(true)
    try {
      const r = await recadrer(source, cadre)
      if (!r.ok) { setRefusAffiche(r.message); secouer(null); return false }
      maj((x) => ({ ...x, affiche: r.valeur }))
      setCadreRecadre(true)
      return true
    } finally { setPreparation(false) }
  }

  const aller = async (n: number, o: { sansAffiche?: boolean } = {}) => {
    if (n < 0 || n >= ETAPES.length || n === etape || envoi || preparation || fin) return
    if (ETAPES[etape]!.cle === 'affiche' && !o.sansAffiche && !(await afficheValidee())) return
    setPourquoi(null)
    setSens(n > etape ? 'avant' : 'arriere')
    setSortante(etape)
    setEtape(n)
    setMax((m) => Math.max(m, n))
    clearTimeout(minuterie.current)
    minuterie.current = setTimeout(() => setSortante(null), SORTIE_MS)
    corps.current?.scrollTo({ top: 0 })
  }

  const creer = async () => {
    if (!b || envoi) return
    if (blocage) { secouer(blocage); return }
    // Les jalons permettent de sauter des étapes sans les valider : on revalide tout, et on ramène à la première qui bloque.
    const bloquante = premiereEtapeBloquante(b)
    if (bloquante) {
      await aller(ETAPES.findIndex((e) => e.cle === bloquante))
      secouer(erreursEtape(bloquante, b)[0] ?? null)
      return
    }
    const r = corpsCreation(b)
    if ('erreurs' in r) { secouer(r.erreurs[0] ?? null); return }
    const garde = new GardeCles(stockageSession(), moi ?? 'inconnu')
    const corpsEnvoye = r.corps
    const affiche = b.affiche
    setEnvoi(true); verrou.current = true; setErreur(null); setAmbiguites([])
    try {
      const issue = await creerPuisEnvoyerAffiche({
        creer: () => creerSoiree(corpsEnvoye, garde.pour(CLE_CREATION, JSON.stringify(corpsEnvoye))),
        envoyerAffiche: affiche ? (id) => envoyerAfficheRecadree(id, affiche, null, cles) : null,
        quandCreee: (c) => {
          garde.oublier(CLE_CREATION)
          try { stockageSession().removeItem(cleBrouillon) } catch { /* rien à oublier */ }
          setFin({
            soiree_id: c.soiree_id, version: c.version, nom: corpsEnvoye.soiree.nom, vente: b.vente, a: b.programmer_a,
            etat: b.vente === 'brouillon' ? 'brouillon' : 'envoi', erreur: null,
            phrase_vente: phraseVenteImmediate(premiereVenteSurPlace(b, maintenantParis()), 'final'),
            affiche: { etat: affiche ? 'envoi' : 'sans', erreur: null }, affiche_a_envoyer: affiche,
          })
          onCreee?.()
        },
      })
      if (!issue.creation.ok) {
        const e = issue.creation.erreur
        setErreur(e)
        if (e.genre === 'heure_ambigue') setAmbiguites(e.ambiguites)
        secouer(null)
        return
      }
      setFin((f) => (f ? { ...f, affiche: issue.affiche } : f))
      if (b.vente !== 'brouillon') await mettreEnVente({ soiree_id: issue.creation.data.soiree_id, version: issue.creation.data.version, vente: b.vente, a: b.programmer_a }, cles, setFin)
      // La liste a été relue à la création ; l'affiche et la mise en vente arrivent après : on la relit encore, sans quoi sa vignette et son état restent ceux d'avant.
      if (affiche || b.vente !== 'brouillon') onCreee?.()
    } finally { setEnvoi(false); verrou.current = false }
  }

  const suivant = async () => {
    if (!b || fin || envoi || preparation) return
    if (cle === 'recap') { await creer(); return }
    if (blocage) { secouer(blocage); return }
    const e = erreursEtape(cle, b)
    if (e.length > 0) { secouer(e[0]!); return }
    await aller(etape + 1)
  }
  suivantLu.current = () => { void suivant() }

  /** Le choix d'une heure ambiguë (`tarifs.N.vente_fin`) se pose sur le N-ième tarif retenu, retouché à la main. */
  const choisirHeure = (champ: string, valeur: string) => {
    const m = /^tarifs\.(\d+)\.(vente_debut|vente_fin)$/.exec(champ)
    if (!m || !b) return
    const cible = retenus(b)[Number(m[1])]
    if (!cible) return
    const k = m[2] as 'vente_debut' | 'vente_fin'
    maj((x) => ({ ...x, tarifs: x.tarifs.map((t) => (t.cle === cible.cle ? { ...t, saisie: { ...tarifEffectif(t, x.nuit, x.ouverture), [k]: valeur }, touche: { ...t.touche, [k]: true } } : t)) }))
    setAmbiguites((a) => a.filter((x) => x.champ !== champ))
  }

  const recommencer = () => {
    libererSource(source); setSource(null); setCadre(null); setCadreRecadre(true); setRefusAffiche(null)
    setFin(null); setEtape(0); setMax(0); setSens(null); setSortante(null); setRetrouve(false); setErreur(null); setAmbiguites([])
    if (salles && contexte) setB(brouillonInitial(prefs, salles, contexte.nuit_proposee))
  }

  const plusTard = () => {
    libererSource(source); setSource(null); setCadre(null); setCadreRecadre(true); setRefusAffiche(null)
    maj((x) => ({ ...x, affiche: null }))
    void aller(1, { sansAffiche: true })
  }

  /* ─── le corps d'une étape ─── */
  const vue = (i: number): ReactNode => {
    if (!b || !contexte || !salles) return null
    const k = ETAPES[i]!.cle
    if (k === 'affiche') {
      return (
        <>
          {blocage ? <p className="bs-note" role="status">{blocage}</p> : null}
          {retrouve ? (
            <div className="bs-note">
              <span>Un brouillon non envoyé a été retrouvé{b.nom.trim() ? ` : « ${b.nom.trim()} »` : ''}. Vos saisies et l’affiche sont gardées.</span>
              <button type="button" className="lc-btn lc-btn--sm" onClick={recommencer}>
                <RefreshCw size={14} aria-hidden="true" /> Recommencer de zéro
              </button>
            </div>
          ) : null}
          <RecadrageAffiche
            source={source} cadre={cadre} affiche={b.affiche}
            onSource={(s) => { libererSource(source); setSource(s); setCadre(cadreInitial(s.iw, s.ih)); setCadreRecadre(false); setRefusAffiche(null); setPourquoi(null) }}
            onCadre={(c) => { setCadre(c); setCadreRecadre(false); setRefusAffiche(null) }}
            onRetirer={() => { libererSource(source); setSource(null); setCadre(null); setCadreRecadre(true); setRefusAffiche(null); maj((x) => ({ ...x, affiche: null })) }}
            erreur={refusAffiche} onErreur={setRefusAffiche} plusTard={plusTard}
          />
          {rangement !== 'complet' ? <p className="bs-note bs-note--doute" role="status">{PHRASE_RANGEMENT[rangement]}</p> : null}
        </>
      )
    }
    if (k === 'nuit') {
      return (
        <>
          <div className="lc-field">
            <label className="lc-label" htmlFor="bsw-nom">Nom de la soirée</label>
            <input id="bsw-nom" className="lc-input" value={b.nom} maxLength={120} autoFocus placeholder="Ex. : MOTEL CLUB" autoComplete="off"
              onChange={(e) => maj((x) => ({ ...x, nom: e.target.value }))} />
          </div>
          <div className="lc-field">
            <label className="lc-label" htmlFor="bsw-nuit">Nuit</label>
            <input id="bsw-nuit" className="lc-input bs-court" type="date" value={b.nuit} onChange={(e) => maj((x) => ({ ...x, nuit: e.target.value }))} />
            <span className="lc-helper">{jourCivil(b.nuit) ? `La ${nuitEnLettres(b.nuit)}.` : 'Le jour où la soirée commence (avant 8 h du matin, c’est encore la nuit de la veille).'}</span>
          </div>
          {passee ? <p className="bs-note bs-note--doute">{PHRASE_NUIT_PASSEE}</p> : null}
          {doublons.length > 0 ? <p className="bs-note bs-note--doute">{phraseDoublon(b.nom)}</p> : null}
          <div className="bs-grille">
            <div className="lc-field">
              <label className="lc-label" htmlFor="bsw-ouv">Ouverture</label>
              <input id="bsw-ouv" className="lc-input" type="time" step={60} value={b.ouverture} onChange={(e) => maj((x) => ({ ...x, ouverture: e.target.value }))} />
              {/* L'aide ne dit quelque chose que la nuit d'un changement d'heure ; le reste du temps, elle répéterait le champ. */}
              {heureDeNuitLisible(b.nuit, b.ouverture) !== b.ouverture ? <span className="lc-helper">{heureDeNuitLisible(b.nuit, b.ouverture)}</span> : null}
            </div>
            <div className="lc-field">
              <label className="lc-label" htmlFor="bsw-ferm">Fermeture</label>
              <input id="bsw-ferm" className="lc-input" type="time" step={60} value={b.fermeture} onChange={(e) => maj((x) => ({ ...x, fermeture: e.target.value }))} />
              <span className="lc-helper">{heureDeNuitLisible(b.nuit, b.fermeture)} · avant 8 h, c’est le lendemain</span>
            </div>
          </div>
          {dureeMinutes(b.ouverture, b.fermeture) !== null ? <p className="bsw-note">La soirée dure {dureeLisible(dureeMinutes(b.ouverture, b.fermeture)!)} (à l’heure de l’horloge).</p> : null}
          <div className="bs-grille">
            <div className="lc-field">
              <label className="lc-label" htmlFor="bsw-fam">Famille (facultative)</label>
              <input id="bsw-fam" className="lc-input" value={b.famille} maxLength={60} onChange={(e) => maj((x) => ({ ...x, famille: e.target.value }))} />
            </div>
          </div>
          <div className="lc-field">
            <label className="lc-label" htmlFor="bsw-pres">Présentation (facultative)</label>
            <textarea id="bsw-pres" className="lc-input bs-zone" rows={3} maxLength={4000} value={b.presentation} onChange={(e) => maj((x) => ({ ...x, presentation: e.target.value }))} />
          </div>
        </>
      )
    }
    if (k === 'salles') return <EtapeSalles b={b} maj={maj} />
    if (k === 'tarifs') return <EtapeTarifs b={b} maj={maj} tvaDefautBp={tvaDefautBp} />
    if (k === 'ventes') return <EtapeVentes b={b} maj={maj} peutPublier={contexte.actions.publier} />
    return (
      <>
        <Recap b={b} doublon={doublons.length > 0} passee={passee} ambiguites={ambiguites} erreur={erreur} choisirHeure={choisirHeure} tvaDefautBp={tvaDefautBp} />
        {rangement !== 'complet' ? <p className="bs-note bs-note--doute" role="status">{PHRASE_RANGEMENT[rangement]}</p> : null}
      </>
    )
  }

  /* ─── la tête ─── */
  const e = ETAPES[etape]!
  const bandeau = fin ? fin.affiche_a_envoyer : etape > 0 ? b?.affiche ?? null : null
  const progression = fin ? 100 : ((etape + 1) / ETAPES.length) * 100
  const tete = (
    <header className={`bsw-tete${bandeau ? ' bsw-tete--affiche' : ''}`} style={bandeau?.teinte ? ({ ['--bs-teinte' as string]: bandeau.teinte } as CSSProperties) : undefined}>
      {bandeau ? <FondAffiche src={bandeau.fond_url} /> : null}
      <div className="bsw-tete__haut">
        {bandeau ? <NetAffiche className="bsw-tete__affiche" src={bandeau.data_url} alt="" largeur={bandeau.largeur} hauteur={bandeau.hauteur} /> : null}
        <div className="bsw-tete__textes">
          <p className="bsw-surtitre">Nouvelle soirée</p>
          <h2 className="bsw-titre" id="bsw-titre">{fin ? 'C’est fait.' : e.question}</h2>
          <p className="bsw-sous">{fin ? `${NOM_BILLETTERIE} · démo : la soirée ne compte nulle part.` : e.sous}</p>
        </div>
        <button type="button" className="bsw-fermer" aria-label="Fermer" onClick={fermer} disabled={envoi}><X size={16} aria-hidden="true" /></button>
      </div>
      <ol className="bsw-jalons" aria-label="Étapes">
        {ETAPES.map((x, k) => {
          const fait = fin ? true : k < etape
          const actif = !fin && k === etape
          const cliquable = !fin && k <= max && k !== etape
          return (
            <li key={x.cle} className={`bsw-jalon${actif ? ' bsw-jalon--actif' : ''}${fait ? ' bsw-jalon--fait' : ''}${cliquable ? ' bsw-jalon--cliquable' : ''}`}>
              <button type="button" className="bsw-jalon__btn" disabled={!cliquable} aria-current={actif ? 'step' : undefined} onClick={() => { void aller(k) }}>
                <span className="bsw-pastille" aria-hidden="true">
                  <span className="bsw-num">{k + 1}</span>
                  <svg className="bsw-coche" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12.5l5.5 5.5L20 7" /></svg>
                </span>
                <span className="bsw-jalon__txt">{x.titre}</span>
              </button>
            </li>
          )
        })}
      </ol>
      <div className="bsw-barre" role="progressbar" aria-valuemin={0} aria-valuemax={ETAPES.length} aria-valuenow={fin ? ETAPES.length : etape + 1} aria-label="Avancement">
        <span className="bsw-barre__plein" style={{ width: `${progression}%` }}>
          {/* Remontée à chaque étape : l'impulsion se rejoue, la largeur, elle, glisse. */}
          <span className="bsw-barre__impulsion" key={fin ? 'fin' : etape} />
        </span>
      </div>
    </header>
  )

  /* ─── le corps ─── */
  let interieur: ReactNode
  if (erreurPrefs && !lues) {
    // Les réglages de Gestion ne se lisent pas : pas de pré-remplissage en silence avec d'autres prix et d'autres modèles que ceux réglés.
    interieur = (
      <div className="bsw-etape">
        <p className="bs-note bs-note--doute" role="status">
          L’assistant ne peut pas démarrer : ses réglages (modèles de tarifs, horaires proposés) n’ont pas pu être lus, et il ne proposera pas d’autres prix que les vôtres. {erreurPrefs.message}
        </p>
        <div><button type="button" className="lc-btn" onClick={relirePrefs}><RefreshCw size={15} aria-hidden="true" /> Réessayer</button></div>
      </div>
    )
  } else if (erreurLecture && (!salles || !contexte)) {
    interieur = (
      <div className="bsw-etape">
        <p className="bs-note bs-note--doute" role="status">{erreurLecture}</p>
        <div><button type="button" className="lc-btn" onClick={() => setTourLecture((n) => n + 1)}><RefreshCw size={15} aria-hidden="true" /> Réessayer</button></div>
      </div>
    )
  } else if (!pret) {
    interieur = <div className="bsw-etape" aria-busy="true"><div className="so-skel"><span /><span /><span /></div></div>
  } else if (fin) {
    interieur = <EcranFinal fin={fin} setFin={setFin} cles={cles} verrou={verrou} recommencer={recommencer} />
  } else {
    const affichees = sortante !== null && sortante !== etape ? [sortante, etape] : [etape]
    interieur = (
      <>
        {affichees.map((i) => {
          const courante = i === etape
          const classe = courante ? (sens === 'avant' ? ' bsw-entre-avant' : sens === 'arriere' ? ' bsw-entre-arriere' : '') : (sens === 'avant' ? ' bsw-sort-avant' : ' bsw-sort-arriere')
          return (
            <section key={ETAPES[i]!.cle} className={`bsw-etape${classe}`} inert={!courante} aria-hidden={courante ? undefined : true} aria-labelledby={courante ? 'bsw-titre' : undefined}>
              <div className="bsw-etape__contenu lc-stagger" ref={courante ? contenu : undefined}>{vue(i)}</div>
            </section>
          )
        })}
        {pourquoi ? <p className="bsw-pourquoi" role="alert">{pourquoi}</p> : null}
      </>
    )
  }

  const derniere = cle === 'recap'
  return (
    <div className="bsw-fenetre" ref={racine}>
      {tete}
      <div className="bsw-corps" ref={corps}>{interieur}</div>
      <footer className={`bsw-pied${fin ? ' bsw-pied--cache' : ''}`} inert={!!fin}>
        <button type="button" className="lc-btn lc-btn--ghost" disabled={etape === 0 || envoi || preparation || !pret} onClick={() => { void aller(etape - 1) }}>Retour</button>
        <span className="bsw-compteur">Étape {etape + 1} sur {ETAPES.length}</span>
        <button type="button" className={`lc-btn lc-btn--primary${derniere && !envoi ? ' bsw-final' : ''}`} disabled={!pret || envoi || preparation || (derniere && ambiguites.length > 0)} onClick={() => { void suivant() }}>
          {preparation ? 'Préparation…' : envoi ? 'Création…' : derniere ? 'Créer la soirée' : 'Continuer'}
        </button>
      </footer>
    </div>
  )
}

function dureeLisible(min: number): string {
  const h = Math.floor(min / 60); const m = min % 60
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')}`
}

/* ═══════════════════════ les salles ═══════════════════════ */

function EtapeSalles({ b, maj }: { b: BrouillonAssistant; maj: (f: (x: BrouillonAssistant) => BrouillonAssistant) => void }) {
  const ouvertes = b.salles.filter((s) => s.retenue).length
  return (
    <>
      <p className="bsw-note">Touchez une salle pour l’ouvrir ou la fermer cette nuit. La jauge d’une salle est enregistrée, mais rien ne la contrôle : seule la jauge totale limite les ventes.</p>
      <div className="bsw-salles">
        {b.salles.map((s, i) => {
          const il = illustrationSalle(s.nom)
          return (
            <div key={s.code} className={`bsw-salle ${il.classe}${s.retenue ? ' bsw-salle--on' : ''}`}>
              <button type="button" className="bsw-salle__bascule" aria-pressed={s.retenue}
                onClick={() => maj((x) => ({ ...x, salles: x.salles.map((y, j) => (j === i ? { ...y, retenue: !y.retenue } : y)) }))}>
                <span className="bsw-salle__illu">{il.svg}</span>
                <span className="bsw-salle__nom">{s.nom}</span>
                {il.style ? <span className="bsw-salle__style">{il.style}</span> : null}
                <span className="bsw-salle__etat"><span className="bsw-pt" aria-hidden="true" />{s.retenue ? 'Ouverte' : 'Fermée'}</span>
              </button>
              {s.retenue ? (
                <input className="lc-input bsw-salle__jauge" inputMode="numeric" placeholder="jauge (facultative)" aria-label={`Jauge de ${s.nom}`} value={s.jauge}
                  onChange={(e) => maj((x) => ({ ...x, salles: x.salles.map((y, j) => (j === i ? { ...y, jauge: e.target.value } : y)) }))} />
              ) : null}
            </div>
          )
        })}
      </div>
      <div className="lc-field">
        <label className="lc-label" htmlFor="bsw-jauge">Jauge totale{ouvertes > 0 ? ` · ${ouvertes} salle${ouvertes > 1 ? 's' : ''} ouverte${ouvertes > 1 ? 's' : ''}` : ''}</label>
        <input id="bsw-jauge" className="lc-input bs-court" inputMode="numeric" value={b.jauge_totale} placeholder="à saisir" onChange={(e) => maj((x) => ({ ...x, jauge_totale: e.target.value }))} />
        <span className="lc-helper">C’est elle, et elle seule, qui limite les ventes à la porte. Elle ne descendra jamais sous les places occupées.</span>
      </div>
    </>
  )
}

/* ═══════════════════════ les tarifs ═══════════════════════ */

function EtapeTarifs({ b, maj, tvaDefautBp }: { b: BrouillonAssistant; maj: (f: (x: BrouillonAssistant) => BrouillonAssistant) => void; tvaDefautBp: number | null }) {
  const [ouverts, setOuverts] = useState<Set<string>>(() => new Set())
  const sallesRetenues = b.salles.filter((s) => s.retenue).map((s) => ({ code: s.code, nom: s.nom }))
  const majTarif = (cle: string, f: (t: TarifAssistant) => TarifAssistant) => maj((x) => ({ ...x, tarifs: x.tarifs.map((t) => (t.cle === cle ? f(t) : t)) }))
  const basculer = (cle: string) => setOuverts((o) => { const n = new Set(o); if (n.has(cle)) n.delete(cle); else n.add(cle); return n })
  const n = retenus(b).length

  return (
    <>
      <p className="bsw-note">
        {n} tarif{n > 1 ? 's' : ''} retenu{n > 1 ? 's' : ''}. Les prix et les heures viennent des modèles ; les heures suivent la nuit et l’ouverture choisies tant que vous n’y touchez pas.
        Une soirée sans tarif se crée, mais ne se met pas en vente.
      </p>
      <div className="bsw-tarifs">
        {b.tarifs.map((t) => {
          const effectif = tarifEffectif(t, b.nuit, b.ouverture)
          const erreurs = t.retenu ? erreursTarifAssistant(b, t) : {}
          const enErreur = Object.keys(erreurs).length > 0
          const ouvert = t.retenu && (ouverts.has(t.cle) || enErreur)
          const avertissements = t.retenu ? avertissementsTarifAssistant(b, t) : []
          // Seulement ce qui reste vide : un quota saisi ne doit plus être « à saisir ».
          const resteASaisir = t.a_saisir.filter((c) => !(c === 'prix_cents' ? t.saisie.prix : t.saisie.quota).trim())
          return (
            <article key={t.cle} className={`bsw-tarif${t.retenu ? ' bsw-tarif--on' : ''}${enErreur ? ' bsw-tarif--erreur' : ''}${ouvert ? ' bsw-tarif--ouvert' : ''}`}>
              <header className="bsw-tarif__tete">
                <Checkbox checked={t.retenu} onChange={(on) => majTarif(t.cle, (x) => ({ ...x, retenu: on }))}
                  label={<span className="bs-tarif__nom"><strong>{effectif.nom.trim() || t.libelle}</strong><span className="lc-hint">{resumeSaisie(effectif)}</span></span>} />
                <span className="bs-tarif__boutons">
                  {t.retenu ? (
                    <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" aria-expanded={ouvert} onClick={() => basculer(t.cle)}>
                      {ouvert ? <><ChevronUp size={14} aria-hidden="true" /> Replier</> : <><ChevronDown size={14} aria-hidden="true" /> Régler</>}
                    </button>
                  ) : null}
                  {t.modele_id === null ? (
                    <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm lc-btn--danger-text" onClick={() => maj((x) => ({ ...x, tarifs: x.tarifs.filter((y) => y.cle !== t.cle) }))}>
                      <Trash2 size={14} aria-hidden="true" /> Retirer
                    </button>
                  ) : null}
                </span>
              </header>
              {resteASaisir.length > 0 && t.retenu ? <p className="lc-hint bsw-tarif__a-saisir">À saisir : {resteASaisir.map((c) => (c === 'prix_cents' ? 'le prix' : 'le quota')).join(' et ')}.</p> : null}
              {ouvert ? (
                <TarifChamps
                  valeur={effectif} mode="creation" salles={sallesRetenues} erreurs={erreurs}
                  tvaDefaut={tvaDefautBp !== null ? tvaLisible(tvaDefautBp) : null}
                  origineHeures={{
                    vente_debut: t.touche.vente_debut ? null : origine(t.relatif.vente_debut),
                    vente_fin: t.touche.vente_fin ? null : origine(t.relatif.vente_fin),
                  }}
                  onChange={(s) => majTarif(t.cle, (x) => ({
                    ...x, saisie: s,
                    touche: { vente_debut: x.touche.vente_debut || s.vente_debut !== effectif.vente_debut, vente_fin: x.touche.vente_fin || s.vente_fin !== effectif.vente_fin },
                  }))}
                />
              ) : null}
              {avertissements.length > 0 ? <ul className="bs-avertissements">{avertissements.map((a) => <li key={a}>{a}</li>)}</ul> : null}
            </article>
          )
        })}
      </div>
      <div>
        <button type="button" className="lc-btn" onClick={() => maj((x) => ({ ...x, tarifs: [...x.tarifs, tarifLibre(`libre-${Date.now().toString(36)}`)] }))}>
          <Plus size={15} aria-hidden="true" /> Ajouter un tarif libre
        </button>
      </div>
    </>
  )
}

const origine = (h: TarifAssistant['relatif']['vente_debut']): string | null => (h === null ? null : heureRelativeLisible(h))

/* ═══════════════════════ la mise en vente ═══════════════════════ */

function EtapeVentes({ b, maj, peutPublier }: { b: BrouillonAssistant; maj: (f: (x: BrouillonAssistant) => BrouillonAssistant) => void; peutPublier: boolean }) {
  const options: { v: ChoixVente; titre: string; texte: string; publier: boolean; icone: ReactNode }[] = [
    { v: 'brouillon', titre: 'Garder en brouillon', texte: 'Rien ne se vend. Vous la mettrez en vente depuis sa fiche, quand vous voudrez.', publier: false, icone: <FileEdit size={22} aria-hidden="true" /> },
    { v: 'programmer', titre: 'Programmer la mise en vente', texte: 'La billetterie la met en vente seule à l’heure choisie, même si Gestion est gelé ou en panne. Pour l’empêcher, il faudra la remettre en brouillon avant.', publier: true, icone: <CalendarClock size={22} aria-hidden="true" /> },
    // Ce que la caisse vend vraiment : un tarif sur place qui ne commence qu'à l'ouverture ne se vend pas « aussitôt ».
    { v: 'maintenant', titre: 'Mettre en vente tout de suite', texte: phraseVenteImmediate(premiereVenteSurPlace(b, maintenantParis()), 'option'), publier: true, icone: <Rocket size={22} aria-hidden="true" /> },
  ]
  return (
    <>
      <div className="bsw-choix" role="radiogroup" aria-label="Après la création">
        {options.map((o) => {
          const interdit = o.publier && !peutPublier
          return (
            <button key={o.v} type="button" role="radio" aria-checked={b.vente === o.v} disabled={interdit}
              className={`bsw-choix__tuile bsw-choix__tuile--${o.v}${b.vente === o.v ? ' bsw-choix__tuile--on' : ''}`} onClick={() => maj((x) => ({ ...x, vente: o.v }))}>
              <span className="bsw-choix__icone">{o.icone}</span>
              <span className="bsw-choix__txt">
                <strong>{o.titre}</strong>
                <span>{interdit ? `Il vous faut le droit « ${DROIT_PUBLIER} ».` : o.texte}</span>
              </span>
              <svg className="bsw-choix__coche" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12.5l5.5 5.5L20 7" /></svg>
            </button>
          )
        })}
      </div>
      {b.vente === 'programmer' ? (
        <div className="lc-field">
          <label className="lc-label" htmlFor="bsw-prog">Jour et heure de la mise en vente (heure de Paris)</label>
          <input id="bsw-prog" className="lc-input bs-court" type="datetime-local" step={60} value={b.programmer_a.slice(0, 16)} onChange={(e) => maj((x) => ({ ...x, programmer_a: e.target.value }))} />
          <span className="lc-helper">Avant la fin de la soirée. La billetterie vérifie l’heure au moment du geste.</span>
        </div>
      ) : null}
      {b.vente !== 'brouillon' ? (
        <ul className="bs-consequences">
          <li>La création part d’abord, en brouillon ; la mise en vente suit, avec une confirmation d’identité. Si elle échoue, la soirée reste créée, en brouillon, et l’écran le dit.</li>
          <li>Dès le premier billet vendu : la nuit ne change plus, aucune salle ne se retire, la soirée ne revient plus en brouillon et ne s’annule plus.</li>
          {rienAVendre(b) ? <li className="bs-attention">Aucun tarif ne se vend à la porte : la soirée sera en vente sans rien à vendre.</li> : null}
        </ul>
      ) : null}
    </>
  )
}

/* ═══════════════════════ tout est prêt ═══════════════════════ */

function Recap({ b, doublon, passee, ambiguites, erreur, choisirHeure, tvaDefautBp }: {
  b: BrouillonAssistant; doublon: boolean; passee: boolean; ambiguites: Ambiguite[]; erreur: ErreurBM | null
  choisirHeure: (champ: string, valeur: string) => void
  /** Le réglage de la billetterie, lu ; null tant qu'il ne l'est pas (« réglage par défaut », sans chiffre). */
  tvaDefautBp: number | null
}) {
  const t = retenus(b)
  const noms = t.map((x) => x.saisie.nom.trim())
  const salles = b.salles.filter((s) => s.retenue)
  const a = b.affiche
  return (
    <>
      {erreur && ambiguites.length === 0 ? <p className="bs-note bs-note--ko" role="alert">{erreur.message}</p> : null}
      {ambiguites.length > 0 ? (
        <div className="bs-choix" role="group" aria-label="Préciser les heures">
          <p>Cette nuit-là, l’heure change : précisez ces heures, puis recréez.</p>
          {ambiguites.map((x) => (
            <div key={x.champ} className="bs-choix__ligne">
              <span>{champLisible(x.champ, noms)}</span>
              <span className="bs-choix__boutons">
                {x.possibles.map((p) => <button key={p} type="button" className="lc-btn lc-btn--sm" onClick={() => choisirHeure(x.champ, p)}>{libelleChoixHeure(p)}</button>)}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <div className={`bsw-recap${a ? ' bsw-recap--affiche' : ''}`} style={a?.teinte ? ({ ['--bs-teinte' as string]: a.teinte } as CSSProperties) : undefined}>
        {a ? <FondAffiche src={a.fond_url} /> : null}
        {a ? <NetAffiche className="bsw-recap__affiche" src={a.data_url} alt="L’affiche de la soirée" largeur={a.largeur} hauteur={a.hauteur} /> : null}
        <div className="bsw-recap__textes">
          <span className="bsw-recap__nuit">{nuitEnLettres(b.nuit)}</span>
          <strong className="bsw-recap__titre">{b.nom.trim() || 'Soirée sans nom'}</strong>
          <span className="bsw-recap__horaires">{heureDeNuitLisible(b.nuit, b.ouverture)} → {heureDeNuitLisible(b.nuit, b.fermeture)}</span>
        </div>
      </div>

      {passee ? <p className="bs-note bs-note--doute">{PHRASE_NUIT_PASSEE}</p> : null}
      {doublon ? <p className="bs-note bs-note--doute">{phraseDoublon(b.nom)}</p> : null}
      {rienAVendre(b) ? <p className="bs-note bs-note--doute">Aucun tarif ne se vend à la porte : la soirée sera en vente sans rien à vendre.</p> : null}

      <dl className="bsw-lignes">
        <div><dt>Salles</dt><dd>{salles.map((s) => (s.jauge.trim() ? `${s.nom} (${s.jauge.trim()})` : s.nom)).join(' · ')}</dd></div>
        <div><dt>Jauge totale</dt><dd>{b.jauge_totale.trim()}</dd></div>
        {b.famille.trim() ? <div><dt>Famille</dt><dd>{b.famille.trim()}</dd></div> : null}
        <div><dt>Affiche</dt><dd>{a ? `${a.largeur.toLocaleString('fr-FR')} × ${a.hauteur.toLocaleString('fr-FR')}, envoyée juste après la création` : 'Plus tard, depuis la fiche'}</dd></div>
        <div><dt>Ensuite</dt><dd>{b.vente === 'brouillon' ? 'Gardée en brouillon' : b.vente === 'maintenant' ? 'Mise en vente tout de suite' : `Mise en vente programmée le ${PARIS_RX.test(b.programmer_a) ? parisLisible(b.programmer_a) : '—'}`}</dd></div>
      </dl>

      <h3 className="lc-section-title">Tarifs ({t.length})</h3>
      {t.length === 0 ? <p className="gd-calm">Aucun tarif : la soirée se crée, mais ne se mettra pas en vente sans au moins un tarif.</p> : (
        <div className="bs-defile" tabIndex={0} role="region" aria-label="Tarifs de la soirée">
          <table className="lc-table bs-table">
            <thead><tr><th>Tarif</th><th>Canal</th><th>Prix</th><th>TVA</th><th>Quota</th><th>Vente</th></tr></thead>
            <tbody>
              {t.map((x) => {
                const s = tarifEffectif(x, b.nuit, b.ouverture)
                const prix = centsDepuisSaisie(s.prix)
                const tva = bpDepuisSaisie(s.tva)
                return (
                  <tr key={x.cle}>
                    <td>{s.nom.trim()}{s.cascade_groupe.trim() ? <span className="lc-hint"> · {s.cascade_groupe.trim()} {s.cascade_rang.trim()}</span> : null}</td>
                    <td>{CANAL_LIBELLE[s.canal]}</td>
                    <td className="bsw-insecable">{typeof prix === 'number' ? euros(prix) : '—'}</td>
                    <td>
                      {typeof tva === 'number' ? <span className="bsw-insecable">{tvaLisible(tva)}</span>
                        : typeof tvaDefautBp === 'number' ? <><span className="bsw-insecable">{tvaLisible(tvaDefautBp)}</span><span className="lc-hint bsw-tva-defaut"> par défaut</span></>
                        : <span className="lc-hint">réglage par défaut</span>}
                    </td>
                    <td>{s.quota.trim() || 'sans'}</td>
                    <td>{s.vente_debut ? `dès ${parisLisible(s.vente_debut)}` : 'dès la mise en vente'}{s.vente_fin ? ` · jusqu’au ${parisLisible(s.vente_fin)}` : ''}{s.entree_avant ? ` · entrée avant ${heureDeNuitLisible(b.nuit, s.entree_avant)}` : ''}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <ul className="bs-consequences">
        <li>La soirée et ses tarifs se créent en un seul envoi, toujours en brouillon{a ? ' ; l’affiche suit, par son propre envoi' : ''}.</li>
        <li>Chaque taux de TVA est figé au premier billet émis sur son tarif. Les formules avec boissons comprises restent à confirmer par la compta.</li>
        <li>Pendant la démo, la soirée ne compte nulle part : ni calendrier, ni compta, ni Accueil, ni « Ce soir ».</li>
      </ul>
    </>
  )
}

/* ═══════════════════════ l'écran final ═══════════════════════ */

async function mettreEnVente(f: { soiree_id: string; version: number; vente: ChoixVente; a: string }, cles: GardeCles, setFin: Dispatch<SetStateAction<Fin | null>>): Promise<void> {
  const geste = f.vente === 'maintenant' ? 'publier' : 'programmer'
  const corps: Record<string, unknown> = geste === 'publier' ? { version: f.version } : { version: f.version, a: f.a }
  const ecran = `vente:${f.soiree_id}`
  const r = await gesteSoiree(f.soiree_id, geste, corps, cles.pour(ecran, JSON.stringify(corps)))
  if (r.ok) { cles.oublier(ecran); setFin((x) => (x ? { ...x, etat: 'faite', erreur: null } : x)) } else setFin((x) => (x ? { ...x, etat: 'echec', erreur: r.erreur } : x))
}

function EcranFinal({ fin, setFin, cles, verrou, recommencer }: {
  fin: Fin; setFin: Dispatch<SetStateAction<Fin | null>>; cles: GardeCles; verrou: MutableRefObject<boolean>; recommencer: () => void
}) {
  const [a, setA] = useState(fin.a)
  const [occupe, setOccupe] = useState(false)
  const pendant = async (f: () => Promise<void>) => {
    setOccupe(true); verrou.current = true
    try { await f() } finally { setOccupe(false); verrou.current = false }
  }
  const reessayerVente = (valeur?: string) => {
    const at = valeur ?? a
    setFin((x) => (x ? { ...x, a: at, etat: 'envoi', erreur: null } : x))
    void pendant(() => mettreEnVente({ ...fin, a: at }, cles, setFin))
  }
  // L'affiche SEULE : jamais la soirée, qui existe déjà.
  const reessayerAffiche = () => {
    const x = fin.affiche_a_envoyer
    if (!x) return
    setFin((f) => (f ? { ...f, affiche: { etat: 'envoi', erreur: null } } : f))
    void pendant(async () => {
      const e = await envoyerAfficheSeule(() => envoyerAfficheRecadree(fin.soiree_id, x, null, cles))
      setFin((f) => (f ? { ...f, affiche: e } : f))
    })
  }
  const ambiguite = fin.erreur?.genre === 'heure_ambigue' ? fin.erreur.ambiguites[0] ?? null : null
  const titre = useMemo(() => {
    if (fin.vente === 'brouillon') return 'Soirée créée en brouillon'
    if (fin.etat === 'faite') return fin.vente === 'maintenant' ? 'Soirée créée et mise en vente' : 'Soirée créée, mise en vente programmée'
    return 'Soirée créée en brouillon'
  }, [fin])
  const x = fin.affiche_a_envoyer
  return (
    <section className="bsw-etape bsw-lancee lc-stagger" aria-live="polite" style={x?.teinte ? ({ ['--bs-teinte' as string]: x.teinte } as CSSProperties) : undefined}>
      <div className="bsw-halo" aria-hidden="true" />
      {x ? <NetAffiche className="bsw-lancee__affiche" src={x.data_url} alt="L’affiche de la soirée" largeur={x.largeur} hauteur={x.hauteur} /> : null}
      <p className="bsw-lancee__titre">{titre}</p>
      <p className="bsw-lancee__nom">{fin.nom}</p>
      <div className="bsw-lancee__suite">
        {fin.vente === 'brouillon' ? <p className="lc-hint">Rien ne se vend. Mettez-la en vente depuis sa fiche, quand ses tarifs sont prêts.</p> : null}
        {fin.etat === 'envoi' ? <p className="lc-hint" aria-busy="true"><ShieldCheck size={14} aria-hidden="true" /> {fin.vente === 'maintenant' ? 'Mise en vente…' : 'Programmation de la mise en vente…'} Gestion peut vous demander de confirmer votre identité.</p> : null}
        {fin.etat === 'faite' && fin.vente === 'programmer' ? <p className="lc-hint">La billetterie la mettra en vente seule le {parisLisible(fin.a)}.</p> : null}
        {fin.etat === 'faite' && fin.vente === 'maintenant' ? <p className="lc-hint">{fin.phrase_vente}</p> : null}
        {fin.etat === 'echec' && fin.erreur ? (
          <div className="bs-note bs-note--ko" role="alert">
            <span>La mise en vente n’a pas été faite : {fin.erreur.message}</span>
            {ambiguite ? (
              <span className="bs-choix__boutons">
                {ambiguite.possibles.map((p) => <button key={p} type="button" className="lc-btn lc-btn--sm" disabled={occupe} onClick={() => { setA(p); reessayerVente(p) }}>{libelleChoixHeure(p)}</button>)}
              </span>
            ) : (
              <>
                {fin.vente === 'programmer' ? (
                  <input className="lc-input bs-court" type="datetime-local" step={60} aria-label="Nouvelle heure de mise en vente" value={a.slice(0, 16)} onChange={(e) => setA(e.target.value)} />
                ) : null}
                <button type="button" className="lc-btn lc-btn--sm" disabled={occupe} onClick={() => reessayerVente()}>Réessayer</button>
              </>
            )}
          </div>
        ) : null}
        {fin.affiche.etat === 'envoi' ? <p className="lc-hint" aria-busy="true">Envoi de l’affiche…</p> : null}
        {fin.affiche.etat === 'faite' ? <p className="lc-hint">Affiche posée : elle apparaît sur la fiche et dans les listes.</p> : null}
        {fin.affiche.etat === 'echec' ? (
          <div className="bs-note bs-note--ko" role="alert">
            <span><strong>{PHRASE_AFFICHE_NON_ENVOYEE}</strong>{fin.affiche.erreur ? ` ${fin.affiche.erreur.message}` : ''}</span>
            <button type="button" className="lc-btn lc-btn--sm" disabled={occupe} onClick={reessayerAffiche}><RefreshCw size={14} aria-hidden="true" /> Réessayer</button>
          </div>
        ) : null}
      </div>
      <div className="bsw-lancee__actions">
        <Link href={`/soirees/billetterie/${fin.soiree_id}`} className="lc-btn lc-btn--primary">Ouvrir la fiche</Link>
        <button type="button" className="lc-btn lc-btn--ghost" disabled={occupe} onClick={recommencer}><Plus size={15} aria-hidden="true" /> Créer une autre soirée</button>
      </div>
    </section>
  )
}
